import { useState, useCallback } from 'react';
import { GlobalButton } from './GlobalButton';
import { GlobalTextarea } from './GlobalTextarea';
import {
  extractProjectPhases,
  extractProjectPhasesErrorMessage,
  type ExtractedProjectPhase,
} from '../lib/extractProjectPhases';
import { formatDateShort } from '../utils/formatters';
import { newId } from '../utils/id';
import type { RealEstateProjectPhase, RealEstateProjectTask, ProjectPhaseStatus } from '../types';
import styles from './ProjectTextImportSidebar.module.css';

type Props = {
  existingPhases: RealEstateProjectPhase[];
  onImport: (phases: RealEstateProjectPhase[], tasks: RealEstateProjectTask[]) => void;
  onCancel: () => void;
};

const STATUS_LABELS: Record<ProjectPhaseStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  blocked: 'Blocked',
};

const STATUS_COLORS: Record<ProjectPhaseStatus, string> = {
  pending: '#888',
  in_progress: '#3b82f6',
  completed: '#22c55e',
  blocked: '#ef4444',
};

export function ProjectTextImportSidebar({ existingPhases, onImport, onCancel }: Props) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extractedPhases, setExtractedPhases] = useState<ExtractedProjectPhase[] | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [selectedPhases, setSelectedPhases] = useState<Set<number>>(new Set());

  const handleExtract = useCallback(async () => {
    if (!text.trim() || loading) return;

    setError(null);
    setLoading(true);
    setExtractedPhases(null);
    setSummary(null);

    try {
      const result = await extractProjectPhases(text);
      setExtractedPhases(result.phases);
      setSummary(result.summary);
      setSelectedPhases(new Set(result.phases.map((_, i) => i)));
    } catch (err) {
      setError(extractProjectPhasesErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [text, loading]);

  const togglePhaseSelection = useCallback((index: number) => {
    setSelectedPhases((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const handleImport = useCallback(() => {
    if (!extractedPhases) return;

    const now = new Date().toISOString();
    const existingPhaseNames = existingPhases.map((p) => p.name.toLowerCase());
    const newPhases: RealEstateProjectPhase[] = [];
    const newTasks: RealEstateProjectTask[] = [];
    const phaseNameToId = new Map<string, string>();

    for (let i = 0; i < extractedPhases.length; i++) {
      if (!selectedPhases.has(i)) continue;

      const extracted = extractedPhases[i];
      if (existingPhaseNames.includes(extracted.name.toLowerCase())) {
        continue;
      }

      const phaseId = newId();
      phaseNameToId.set(extracted.name, phaseId);

      const dependsOn: string[] = [];
      if (extracted.dependsOnPhaseNames) {
        for (const depName of extracted.dependsOnPhaseNames) {
          const existingDep = existingPhases.find(
            (p) => p.name.toLowerCase() === depName.toLowerCase()
          );
          if (existingDep) {
            dependsOn.push(existingDep.id);
          } else {
            const newDepId = phaseNameToId.get(depName);
            if (newDepId) {
              dependsOn.push(newDepId);
            }
          }
        }
      }

      const phase: RealEstateProjectPhase = {
        id: phaseId,
        name: extracted.name,
        status: extracted.status,
        dependsOn: dependsOn.length > 0 ? dependsOn : undefined,
        expectedStartDate: extracted.expectedStartDate,
        expectedEndDate: extracted.expectedEndDate,
        responsibleParty: extracted.responsibleParty,
        notes: extracted.notes,
        sortOrder: existingPhases.length + newPhases.length,
        createdAt: now,
        updatedAt: now,
      };

      newPhases.push(phase);

      if (extracted.suggestedTasks) {
        for (const taskTitle of extracted.suggestedTasks) {
          const task: RealEstateProjectTask = {
            id: newId(),
            phaseId,
            title: taskTitle,
            done: false,
            createdAt: now,
            completedAt: null,
          };
          newTasks.push(task);
        }
      }
    }

    onImport(newPhases, newTasks);
  }, [extractedPhases, selectedPhases, existingPhases, onImport]);

  const selectedCount = selectedPhases.size;
  const canImport = extractedPhases && selectedCount > 0;

  return (
    <div className="sidebarFormStack">
      <h2 className="sidebarFormTitle">Import from text</h2>
      <p className="bankProviderConnectIntro">
        Paste an email, project update, or meeting notes. AI will extract project phases,
        timelines, and dependencies.
      </p>

      {!extractedPhases ? (
        <>
          <GlobalTextarea
            label="Project text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste your email, project update, or meeting notes here..."
            rows={12}
            maxLength={50000}
          />
          <p className="muted" style={{ marginTop: -8, fontSize: '0.8125rem' }}>
            {text.length.toLocaleString()} / 50,000 characters
          </p>

          {error ? <p className="sidebarFormError">{error}</p> : null}

          <div className="sidebarFormActions">
            <GlobalButton type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </GlobalButton>
            <GlobalButton
              type="button"
              variant="primary"
              isLoading={loading}
              disabled={loading || text.trim().length < 50}
              onClick={handleExtract}
            >
              Extract phases
            </GlobalButton>
          </div>
        </>
      ) : (
        <>
          {summary ? (
            <div className={styles.summaryBox}>
              <strong>Summary:</strong> {summary}
            </div>
          ) : null}

          <div className={styles.phaseList}>
            <p className={styles.phaseListHeader}>
              {extractedPhases.length} phase{extractedPhases.length !== 1 ? 's' : ''} found
              {selectedCount > 0
                ? ` · ${selectedCount} selected`
                : ' · None selected'}
            </p>

            {extractedPhases.map((phase, index) => {
              const isSelected = selectedPhases.has(index);
              const isDuplicate = existingPhases.some(
                (p) => p.name.toLowerCase() === phase.name.toLowerCase()
              );

              return (
                <label
                  key={index}
                  className={`${styles.phaseItem} ${isDuplicate ? styles.phaseItemDuplicate : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected && !isDuplicate}
                    disabled={isDuplicate}
                    onChange={() => togglePhaseSelection(index)}
                  />
                  <div className={styles.phaseContent}>
                    <div className={styles.phaseHeader}>
                      <span
                        className={styles.statusDot}
                        style={{ backgroundColor: STATUS_COLORS[phase.status] }}
                      />
                      <span className={styles.phaseName}>{phase.name}</span>
                      <span className={styles.phaseStatus}>
                        {STATUS_LABELS[phase.status]}
                      </span>
                    </div>
                    <div className={styles.phaseMeta}>
                      {phase.expectedStartDate || phase.expectedEndDate ? (
                        <span>
                          {formatDateShort(phase.expectedStartDate)}
                          {phase.expectedEndDate
                            ? ` → ${formatDateShort(phase.expectedEndDate)}`
                            : ''}
                        </span>
                      ) : null}
                      {phase.responsibleParty ? (
                        <span>{phase.responsibleParty}</span>
                      ) : null}
                    </div>
                    {phase.dependsOnPhaseNames && phase.dependsOnPhaseNames.length > 0 ? (
                      <div className={styles.phaseDeps}>
                        Depends on: {phase.dependsOnPhaseNames.join(', ')}
                      </div>
                    ) : null}
                    {phase.notes ? (
                      <div className={styles.phaseNotes}>{phase.notes}</div>
                    ) : null}
                    {phase.suggestedTasks && phase.suggestedTasks.length > 0 ? (
                      <div className={styles.phaseTasks}>
                        <strong>Tasks:</strong>
                        <ul>
                          {phase.suggestedTasks.map((task, ti) => (
                            <li key={ti}>{task}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {isDuplicate ? (
                      <div className={styles.duplicateBadge}>
                        Already exists — will be skipped
                      </div>
                    ) : null}
                  </div>
                </label>
              );
            })}
          </div>

          {error ? <p className="sidebarFormError">{error}</p> : null}

          <div className="sidebarFormActions">
            <GlobalButton
              type="button"
              variant="secondary"
              onClick={() => {
                setExtractedPhases(null);
                setSummary(null);
                setError(null);
              }}
            >
              Back
            </GlobalButton>
            <GlobalButton
              type="button"
              variant="primary"
              disabled={!canImport}
              onClick={handleImport}
            >
              Import {selectedCount} phase{selectedCount !== 1 ? 's' : ''}
            </GlobalButton>
          </div>
        </>
      )}
    </div>
  );
}
